import { setAuthToken } from '../api/client';

interface PanorUser {
  id: number | string;
  username: string;
}

interface PanorAuthGlobal {
  getUser: () => PanorUser | null;
  getToken: () => string | null;
  requireLogin: (message?: string) => Promise<PanorUser>;
  logout: () => void;
  onAuth: (cb: (user: PanorUser | null) => void) => void;
  save: (service: string, data: unknown) => Promise<void>;
  load: (service: string) => Promise<unknown>;
}

declare global {
  interface Window {
    PanorAuth?: PanorAuthGlobal;
  }
}

let readyPromise: Promise<PanorAuthGlobal> | null = null;

export function panorReady(): Promise<PanorAuthGlobal> {
  if (readyPromise) return readyPromise;
  readyPromise = new Promise((resolve, reject) => {
    const start = Date.now();
    const check = () => {
      if (window.PanorAuth) { resolve(window.PanorAuth); return; }
      if (Date.now() - start > 10_000) { reject(new Error('PanorAuth SDK failed to load')); return; }
      setTimeout(check, 50);
    };
    check();
  });
  return readyPromise;
}

/** Show the login modal (or resolve immediately if already logged in). */
export async function requireLogin(message?: string): Promise<PanorUser> {
  const auth = await panorReady();
  const user = await auth.requireLogin(message);
  syncToken();
  return user;
}

export async function getCurrentUser(): Promise<PanorUser | null> {
  const auth = await panorReady();
  return auth.getUser();
}

/** Sync the SDK's stored token into our api client. Call after login + on app boot. */
export function syncToken(): boolean {
  const t = window.PanorAuth?.getToken() ?? null;
  setAuthToken(t ?? '');
  return !!t;
}

export async function logout(): Promise<void> {
  const auth = await panorReady();
  auth.logout();
  setAuthToken('');
}

/** Subscribe to login/logout. */
export async function onAuthChange(cb: (user: PanorUser | null) => void): Promise<void> {
  const auth = await panorReady();
  auth.onAuth(user => {
    setAuthToken(window.PanorAuth?.getToken() ?? '');
    cb(user);
  });
}
